'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLuckyNumbers } from '../hooks/useLuckyNumbers'
import CoinGlyph from '../components/lucky/CoinGlyph'
import { showToast } from '../components/ui/Toast'
import { coinNameKey, formatDuration } from '../utils/helpers'
import type { LuckyPickResult } from '../types'
import { useLocale, useT } from '../contexts/LocaleContext'

function gridColumns(poolSize: number) {
  if (poolSize >= 100) return 10
  if (poolSize >= 50) return 10
  if (poolSize >= 30) return 6
  return 5
}

function adsLabel(adsRemaining: number | undefined, maxAds: number | undefined, unlimited: string) {
  if (adsRemaining === undefined) return ''
  if (adsRemaining < 0 || (maxAds !== undefined && maxAds < 0)) return unlimited
  return String(adsRemaining)
}

export default function LuckyNumbersPage() {
  const navigate = useNavigate()
  const t = useT()
  const { dir } = useLocale()
  const { status, loading, error, busy, start, submitPick, watchAd, refetch, setError } = useLuckyNumbers()
  const [selected, setSelected] = useState<number[]>([])
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [loseResult, setLoseResult] = useState<LuckyPickResult | null>(null)
  const autoStarted = useRef(false)
  const timedOutRef = useRef(false)

  useEffect(() => {
    if (error) showToast(error, 'error')
  }, [error])

  useEffect(() => {
    if (!status?.is_window_open || status.session_started || autoStarted.current) return
    autoStarted.current = true
    start()
  }, [status?.is_window_open, status?.session_started, start])

  useEffect(() => {
    setSelected([])
  }, [status?.current_stage, status?.stage?.numbers])

  useEffect(() => {
    if (status?.demoted) {
      showToast(t('lucky.toastDemotedStageChance'), 'error')
    }
  }, [status?.demoted, status?.current_stage, t])

  useEffect(() => {
    if (!status?.session_active) {
      setSecondsLeft(status?.seconds_remaining ?? 0)
      return
    }
    timedOutRef.current = false
    setSecondsLeft(status.seconds_remaining)
    const id = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (!timedOutRef.current) {
            timedOutRef.current = true
            refetch()
          }
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [status?.session_active, status?.seconds_remaining, refetch])

  const stage = status?.stage
  const pickLimit = stage?.pick_count ?? 0
  const numbers = useMemo(
    () => (stage?.numbers ? [...stage.numbers].sort((a, b) => a - b) : []),
    [stage?.numbers],
  )
  const canConfirm =
    !!stage &&
    selected.length === stage.pick_count &&
    !!status?.session_active &&
    (status?.attempts_remaining ?? 0) > 0 &&
    !busy

  const toggleNumber = (n: number) => {
    if (busy || !status?.session_active || status.completed) return
    if ((status.attempts_remaining ?? 0) <= 0) return
    setSelected((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n)
      if (prev.length >= pickLimit) return prev
      return [...prev, n]
    })
  }

  const onSubmit = async () => {
    if (!stage || selected.length !== stage.pick_count) {
      showToast(t('lucky.toastPickExact', { n: stage?.pick_count ?? 0 }), 'error')
      return
    }
    const next = await submitPick(selected)
    if (!next) return
    setSelected([])
    if (!next.won) {
      setLoseResult(next)
      return
    }
    showToast(next.message, 'success')
  }

  const closeLoseDialog = () => setLoseResult(null)

  const onWatchAd = async () => {
    setError(null)
    const data = await watchAd()
    if (data) {
      if (data.demoted) {
        showToast(t('lucky.toastDemotedStage'), 'error')
      } else {
        showToast(t('lucky.toastAdCharged'), 'success')
      }
    }
  }

  if (loading && !status) {
    return (
      <div className="lucky-game mini-app-container pb-20 flex items-center justify-center text-[#8fa8d4]">
        {t('common.loading')}
      </div>
    )
  }

  const needAdForChance =
    !!status &&
    !status.completed &&
    status.session_active &&
    status.attempts_remaining <= 0 &&
    status.can_watch_ad

  return (
    <div className="lucky-game mini-app-container pb-20">
      <div className="p-4 pt-14">
        <div className="lucky-game__top">
          <button type="button" className="lucky-game__back-text" onClick={() => navigate('/games')} aria-label={t('common.back')}>
            {dir === 'rtl' ? '→' : '←'}
          </button>
          <div className="lucky-game__meta">
            <span className="inline-flex items-center gap-1">
              {t('lucky.chancesLabel')} <strong>{status?.attempts_remaining ?? 0}</strong>
            </span>
            <span>·</span>
            <span>
              {status?.session_started
                ? formatDuration(secondsLeft)
                : `${status?.session_minutes ?? 20}:00`}
            </span>
          </div>
        </div>

        <div className="lucky-game__steps" aria-label={t('lucky.stepsAria')}>
          {[1, 2, 3, 4].map((step, i) => {
            const current = status?.current_stage ?? 1
            const done = !!status?.completed || current > step
            const active = !status?.completed && current === step
            return (
              <div key={step} className="contents">
                {i > 0 && (
                  <div className={`lucky-game__step-line ${done || active ? 'is-done' : ''}`} />
                )}
                <div
                  className={`lucky-game__step ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}
                >
                  {step}
                </div>
              </div>
            )
          })}
        </div>

        {!status?.is_window_open ? (
          <div className="lucky-game__panel">
            <p>{t('lucky.windowClosed')}</p>
          </div>
        ) : status?.completed ? (
          <div className="lucky-game__panel">
            <CoinGlyph type="diamond" size={56} />
            <h2 style={{ margin: '10px 0 0' }}>{t('lucky.allDoneTitle')}</h2>
            <p style={{ marginTop: 8, color: '#8fa8d4', fontSize: 13 }}>{t('lucky.allDoneHint')}</p>
          </div>
        ) : status?.session_started && !status.session_active ? (
          <div className="lucky-game__panel">
            <p>
              {(status.current_stage ?? 1) <= 1
                ? t('lucky.sessionExpiredStage1')
                : t('lucky.sessionExpiredRestart')}
            </p>
            <button
              type="button"
              className="lucky-game__confirm is-ready"
              style={{ marginTop: 12 }}
              disabled={busy}
              onClick={() => start()}
            >
              {t('lucky.restart')}
            </button>
          </div>
        ) : stage ? (
          <>
            <div className="lucky-game__hint">
              <h2>
                {t('lucky.hintPick', { pool: stage.pool_size, pick: stage.pick_count })}
              </h2>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9eb6e0' }}>
                {t('lucky.hintOneWinner', { pool: stage.pool_size })}
              </p>
              <p className="inline-flex items-center justify-center gap-1 flex-wrap">
                <CoinGlyph type={stage.reward} size={22} />
                {t('lucky.rewardLine', {
                  coin: t(coinNameKey(stage.reward)),
                  selected: selected.length,
                  pick: stage.pick_count,
                })}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#8fa8d4' }}>
                {stage.stage === 1
                  ? t('lucky.stage1Quota')
                  : `${t('lucky.stageFreePrefix', { free: stage.free_attempts })}${
                      stage.max_ads === 0
                        ? t('lucky.noAds')
                        : t('lucky.adsRemaining', {
                            n: adsLabel(status?.ads_remaining, stage.max_ads, t('lucky.unlimited')),
                          })
                    }${t('lucky.stageTimeLimit')}`}
              </p>
            </div>

            <div className="lucky-game__grid-wrap">
              <div
                className="lucky-game__grid"
                style={{ gridTemplateColumns: `repeat(${gridColumns(stage.pool_size)}, minmax(0, 1fr))` }}
              >
                {numbers.map((n) => {
                  const isOn = selected.includes(n)
                  return (
                    <button
                      key={n}
                      type="button"
                      className={`lucky-game__cell ${isOn ? 'is-on' : ''}`}
                      disabled={busy || !status.session_active || (status.attempts_remaining ?? 0) <= 0}
                      onClick={() => toggleNumber(n)}
                    >
                      {n}
                    </button>
                  )
                })}
              </div>
            </div>

            <button
              type="button"
              className={`lucky-game__confirm ${canConfirm ? 'is-ready' : ''}`}
              disabled={!canConfirm}
              onClick={onSubmit}
            >
              {busy ? '...' : t('lucky.confirmPicks')}
            </button>
          </>
        ) : null}

        {needAdForChance && (
          <div className="lucky-game__panel">
            <p style={{ margin: 0, fontSize: 13 }}>
              {stage?.free_attempts === 0 && (status?.ads_watched ?? 0) === 0
                ? t('lucky.needAdNoFree')
                : t('lucky.needAdRecharge')}
            </p>
          </div>
        )}

        {status && !status.completed && status.can_watch_ad && status.session_active && (
          <button
            type="button"
            className="lucky-game__ad"
            disabled={busy}
            onClick={onWatchAd}
          >
            {t('lucky.watchAdPlus', {
              n: status.stage?.ad_bonus_per_watch ?? (status.current_stage <= 1 ? 5 : 1),
            })}
            {typeof status.ads_remaining === 'number' && status.ads_remaining >= 0
              ? ` (${status.ads_remaining})`
              : ''}
          </button>
        )}
      </div>

      {loseResult && (
        <div className="lucky-lose" role="dialog" aria-modal="true" aria-labelledby="lucky-lose-title">
          <div className="lucky-lose__card">
            <div className="lucky-lose__emoji" aria-hidden="true">
              ✕
            </div>
            <h2 id="lucky-lose-title">{loseResult.demoted ? t('lucky.loseDemotedTitle') : t('lucky.loseTitle')}</h2>
            <p className="lucky-lose__msg">{loseResult.message}</p>
            {loseResult.correct_numbers && loseResult.correct_numbers.length > 0 && (
              <div className="lucky-lose__answer">
                <span>{t('lucky.correctWas')}</span>
                <div className="lucky-lose__nums">
                  {loseResult.correct_numbers.map((n) => (
                    <strong key={n}>{n}</strong>
                  ))}
                </div>
              </div>
            )}
            <p className="lucky-lose__hint">
              {loseResult.demoted
                ? t('lucky.loseDemotedHint')
                : t('lucky.loseNumbersChanged')}
              {typeof loseResult.attempts_remaining === 'number'
                ? ` ${t('lucky.attemptsLeft', { n: loseResult.attempts_remaining })}`
                : ''}
            </p>
            <button type="button" className="lucky-lose__btn" onClick={closeLoseDialog}>
              {loseResult.demoted ? t('lucky.continueStage1') : t('lucky.tryAgain')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
